'use client';

import Dialog from '@/components/next-ui/Dialog';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import CommunityReviews from './CommunityReviews';
import Button from '@/components/next-ui/Button';
import { sanitizeNonProfitAddress } from '@/utils/nonProfitAddress.helper';
import Typography from '@/components/next-ui/Typography';
import { Rating } from '@mui/material';
import TagMentionInput from '../TagMentionInput/TagMentionInput';
import { useAddOrUpdateCommunityReview } from '@/actions/community-review.actions.js';
import { useGetUserCommunityReview } from '@/actions/nonprofit.actions';
import { revalidatePath } from 'next/cache';
import {
  revalidatePage,
  revalidatePageWithType,
} from '@/actions/server.actions';
import { cx } from 'cva';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import useHasUserSession from '@/hooks/useHasUserSession';
import useNonProfitSignUpPopUpStore from '@/components/store/nonProfitSignUpPopUpSlice';

const defaultPayloadObject = {
  nonProfitId: null,
  message: '',
  rating: 0,
  attachment: null,
  reviewId: null,
};

function CommunityReviewsDialog(props) {
  const { slug, onClose, nonProfit, communityRating, refetchNonProfitRating } =
    props;

  // REVIEW MUTATIONS
  const {
    mutateAsync: addOrUpdateReview,
    isLoading: isSaving,
    error,
  } = useAddOrUpdateCommunityReview();

  const {
    data: userReview,
    isLoading: userReviewLoading,
    refetch: refetchUserReview,
  } = useGetUserCommunityReview(nonProfit.id, isSaving, communityRating);

  const isLessThanSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));
  const hasUserSession = useHasUserSession();

  const setShowNonProfitSignUpPopUp = useNonProfitSignUpPopUpStore(
    (state) => state.setOpen,
  );

  const [step, setStep] = useState('reviews');
  const [payload, setPayload] = useState({
    ...defaultPayloadObject,
    nonProfitId: nonProfit.id,
  });
  const [formError, setFormError] = useState('');

  const { totalRating, reviewCount } = communityRating || {};
  const averageRating = useMemo(() => {
    const { totalRating, reviewCount } = communityRating || {};
    return reviewCount > 0
      ? Math.round((totalRating / reviewCount) * 10) / 10
      : 0;
  }, [communityRating]);

  const nonProfitAddress = useMemo(() => {
    return Object.values(sanitizeNonProfitAddress(nonProfit.address, nonProfit))
      .filter(Boolean)
      .join(', ');
  }, [nonProfit.address, nonProfit]);

  const handleSubmit = async () => {
    // If rating is 0 (falsy), set error
    if (!payload.rating) {
      setFormError('Rating is required.');
      return;
    }
    setFormError('');
    await addOrUpdateReview(payload);
    // refetchUserReview();
    // refetchNonProfitRating();
    await Promise.all([refetchUserReview(), refetchNonProfitRating()]);

    setPayload({ ...defaultPayloadObject, nonProfitId: nonProfit.id });
    setStep('success');
    // revalidatePageWithType('/nonprofit/[slug]', 'page');
  };

  useEffect(() => {
    if (userReview) {
      setPayload((prev) => ({
        ...prev,
        rating: userReview.rating,
        message: userReview.message,
        reviewId: userReview.id,
      }));
    }
  }, [userReview]);

  const getDialogTitle = () => {
    switch (step) {
      case 'form':
        return userReview?.id ? 'Edit Review' : 'Add Review';
      case 'reviews':
        return 'Reviews';
      case 'select-image':
        return 'Select Image';
      case 'select-video':
        return 'Select Video';
      default:
        return '';
    }
  };

  const handleRedirect = () => {
    if (!hasUserSession) {
      setShowNonProfitSignUpPopUp(
        true,
        nonProfit,
        `/nonprofit/${slug}?addReview=true`,
      );
    }
  };

  const handleAddReview = () => {
    if (hasUserSession) {
      setStep('form');
    } else {
      handleRedirect();
    }
  };

  return (
    <Dialog
      preventOutsideClose
      isOpen={true}
      maxWidth="lg"
      title={getDialogTitle()}
      contentClass=""
      // titleClass="pb-2"
      onClose={onClose}>
      <div className="flex h-full w-full flex-col overflow-y-auto">
        {step === 'form' && (
          <div className="flex flex-1 flex-col gap-4">
            <Rating
              value={payload.rating}
              precision={0.1}
              onChange={(event, newValue) => {
                if (newValue !== null) {
                  setPayload((prev) => ({ ...prev, rating: newValue }));
                  // Clear form error when rating is selected
                  if (newValue) setFormError('');
                }
              }}
              size="large"
              className="text-primary"
            />
            {formError && (
              <Typography variant="subtitle2" color="error">
                {formError}
              </Typography>
            )}
            <div className="flex-1 rounded-md bg-primary-light-1">
              <TagMentionInput
                value={payload.message}
                onChange={(value) =>
                  setPayload((prev) => ({ ...prev, message: value }))
                }
                placeholder="Write a review..."
                error={error?.message}
                style={{
                  input: { overflow: 'auto', height: '20vh' },
                  highlighter: {
                    overflow: 'hidden',
                    height: '20vh',
                  },
                  control: {
                    fontSize: '16px',
                  },
                }}
              />
            </div>
            <div
              className={cx(
                'flex w-full items-center justify-end gap-4 py-2',
                isLessThanSmallScreen && 'mb-32',
              )}>
              <Button
                color="primary"
                variant="outlined"
                size="md"
                onClick={() => setStep('reviews')}>
                Cancel
              </Button>
              <Button
                disabled={isSaving}
                color="primary"
                variant="filled"
                size="md"
                loading={!!isSaving}
                onClick={handleSubmit}>
                Post
              </Button>
            </div>
          </div>
        )}

        {step === 'reviews' && (
          <div className="flex flex-1 flex-col gap-4">
            <Typography variant="h2">{nonProfit.name}</Typography>
            <Typography variant="subtitle2" color="textSecondary">
              {nonProfitAddress}
            </Typography>
            <div className="my-1 flex flex-row gap-2">
              {averageRating > 0 && (
                <Typography variant="subtitle1" color="textSecondary">
                  {averageRating.toFixed(1)}
                </Typography>
              )}
              <Rating
                name="read-only"
                value={averageRating}
                precision={0.1}
                readOnly
                className="text-primary"
              />
              <Typography variant="subtitle1" color="textSecondary">
                {reviewCount ? reviewCount : '0'} Neki review
                {reviewCount !== 1 ? 's' : ''}
              </Typography>
            </div>
            <div>
              <Button onClick={handleAddReview}>
                {!userReviewLoading && userReview?.id ? 'Edit' : 'Add'} Review
              </Button>
            </div>
            <CommunityReviews
              nonProfit={nonProfit}
              nonProfitId={nonProfit.id}
              communityRating={communityRating}
              handleRedirect={handleRedirect}
              disabled
            />
          </div>
        )}

        {step === 'success' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
            <img
              src={'/assets/images/neki-heart.png'}
              alt="thank-you-heart"
              className="h-24 w-24"
            />
            <Typography variant="h2">
              Thank you for your review and for supporting {nonProfit.name}! 🎉
            </Typography>
            <Typography variant="subtitle1">
              Your feedback makes a difference! By sharing your thoughts, you're
              helping others learn more about {nonProfit.name} and the impact
              they create.
            </Typography>
            <div className="flex w-full items-center justify-center gap-4 py-4">
              <Button
                color="primary"
                variant="outlined"
                size="md"
                onClick={onClose} // Close dialog
              >
                OK
              </Button>
              <Button
                color="primary"
                variant="filled"
                size="md"
                onClick={() => setStep('reviews')} // Go to reviews
              >
                See Your Review
              </Button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default CommunityReviewsDialog;
